import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import { PlacesProvider } from '@awaze/mfe-common.api.places-api'
import {
  IHeaderOptionsModuleFields,
  IPageModuleFields,
} from 'contentful/types/contentful-modules'
import { ContentfulError } from 'contentful/validation/validate-model'
import type { GetStaticPaths, GetStaticProps } from 'next'
import React, { JSX, useEffect, useState } from 'react'
import { ConditionalWrapper } from '@/components/ConditionalWrapper/ConditionalWrapper'
import { CustomHead } from '@/components/CustomHead/CustomHead'
import { LcpImagePreload } from '@/components/ResponsiveImage/LcpImagePreload'
import { TmsProvider } from '@awaze/mfe-common.contexts.tms'
import { GenericPageLayout } from 'src/layouts/GenericPageLayout'
import {
  ParseConfigInterface,
  parseConfig,
} from 'src/lib/contentful/contentful.utils'
import { fetchProperties } from 'src/lib/properties/properties'
import { SupportedBrand } from 'src/lib/properties/properties.types'
import { MessagesProvider } from 'src/localization/MessagesProvider'
import { ModuleErrors } from '@/components/module-errors/ModuleErrors'
import {
  ContentSection,
  Picture,
  getSections,
  removeUndef,
  removeSysAndMetadata,
} from '@/components/content/contentMapper'
import {
  GlobalFields,
  mapGlobalFields,
} from 'src/modules/mappers/globalFieldsMapper'
import { isEmptyPage } from '@/components/is-empty-file/isEmptyPage'
import { BlogTag } from '@/components/BlogTags/mapBlogTag'
import { logger } from 'src/utils/Logger'

import {
  BreadcrumbItems,
  getBreadcrumbFromSlug,
} from 'src/utils/breadcrumb/breadcrumb-utils'
import {
  getUtagData,
  getUtagDataForBlogPage,
} from 'src/utils/dataLayer/utagData'
import {
  AwazeCountry,
  GlobalProps,
  validateHeaders,
} from 'src/utils/globalProps'
import { getCurrentPublicRuntimeConfig } from 'src/utils/helpers/helpers'
import { getPageSchemaMarkup } from 'src/utils/schema-markup/page-schema-markup'
import {
  IFooterFields,
  IHeaderFields,
} from '../../../@types/generated/contentful-global'
import {
  contentfulFactory,
  contentfulGlobalFactory,
  getInterstitialPage,
  getLocaleFromContentful,
  getPageModuleFromContentful,
  getPageSlugFromAllLocales,
} from '../../../contentful/client/contentful-client-helpers'
import { logWarnings } from '../../../src/utils/Logger/log-utils'
import { LocaleSlug } from '@/components/Utils/HrefLangLinks'
import ThumbnailMap from '@/components/ThumbnailMap/ThumbnailMap'
import { useAgencyParamTracker } from '@awaze/mfe-common.utils.agency-param-tracker'
import { GuestAuthProvider } from '@awaze/mfe-common.contexts.guest-auth'
import { ContentWrapper } from '@/components/content/ContentWrapper'

export interface BlogData {
  author: string
  timeToRead: number
  blogTags: BlogTag[]
  createdAt: string
  updatedAt: string
  title: string
}
interface ModulePageProps {
  contentfulId?: string
  isPreview: boolean
  sections?: ContentSection[]
  errors?: ContentfulError[]
  headerOptions?: IHeaderOptionsModuleFields
  awazeHttpHeaders: {
    brand: Brand
    contentfulLocale: string
    marketCode: string
    language: string
    country: AwazeCountry
    currency: string
    newAWS: boolean
    slug: string
    host: string
  }
  brand: Brand
  seoTitle?: IPageModuleFields['seoTitle']
  seoDescription?: IPageModuleFields['seoDescription']
  seoNoIndex?: IPageModuleFields['seoNoindex']
  seoKeywords?: IPageModuleFields['seoKeywords']
  header: IHeaderFields
  footer: IFooterFields
  unparsedSearchConfig: ParseConfigInterface
  lcpImage?: Picture | string
  labels?: Record<string, string>
  optimizelySdkKey: string
  pageType: IPageModuleFields['pageType']
  experiments: Record<string, string> | null
  schemaMarkup?: string
  blogData?: BlogData
  breadcrumbs: BreadcrumbItems[]
  userDeviceType: string
  hrefLangs?: LocaleSlug
  awazeClient: string
  showPhoneIcon: boolean
}

const REVALIDATE_SECONDS = 60

const ContentfulIdentifier: React.FC<{
  isPreview: boolean
  contentfulId?: string
  children?: React.ReactNode
}> = ({ isPreview, contentfulId, children }) => {
  if (!isPreview) {
    return <>{children}</>
  }
  return <div data-contentfulid={contentfulId}>{children}</div>
}

export default function ModulePage({
  contentfulId,
  isPreview,
  sections,
  errors,
  headerOptions,
  awazeHttpHeaders,
  brand,
  seoDescription,
  seoTitle,
  seoNoIndex,
  seoKeywords,
  header,
  footer,
  unparsedSearchConfig,
  lcpImage,
  labels,
  optimizelySdkKey,
  pageType,
  experiments,
  schemaMarkup,
  blogData,
  breadcrumbs,
  userDeviceType,
  hrefLangs,
  awazeClient,
  showPhoneIcon,
}: Readonly<ModulePageProps>): JSX.Element {
  const [session, setSession] = useState(null)

  const searchFormConfig = parseConfig(unparsedSearchConfig)
  const runtimeConfig = getCurrentPublicRuntimeConfig()
  const {
    PLACES_API_URI: placesApiUrl,
    ACQ_PLACES_API_URI: acqPlacesApiUrl,
    AKAMAI_KEY: placesAkamaiKey,
    guestAppTmsId,
    tmsId,
    RETRIEVE_USER_ROUTE,
  } = runtimeConfig

  useEffect(() => {
    fetch(RETRIEVE_USER_ROUTE)
      .then((res) => res?.json())
      .then((data) => {
        setSession(data ?? null)
      })
      .catch(() => {
        setSession(null)
      })
  }, [])
  useAgencyParamTracker()

  const id = awazeClient === 'guest-app' ? guestAppTmsId : tmsId

  return (
    <GuestAuthProvider initialSession={session}>
      <PlacesProvider
        brand={awazeHttpHeaders.brand}
        language={awazeHttpHeaders.contentfulLocale}
        placesApiUri={awazeHttpHeaders.newAWS ? acqPlacesApiUrl : placesApiUrl}
        headers={{ 'x-awaze-akamai': placesAkamaiKey }}
      >
        <TmsProvider
          id={id}
          pageLoadDataObject={{
            awaze_device_type: userDeviceType,
            page_id: contentfulId,
            ...getUtagData({
              blogData,
              awazeHttpHeadersSlug: awazeHttpHeaders?.slug,
              pageType,
            }),
            site_brand: awazeHttpHeaders.brand,
            site_currency: awazeHttpHeaders.currency,
            site_environment: runtimeConfig.APP_ENV,
            site_locale: `${awazeHttpHeaders.language}-${awazeHttpHeaders.country}`,
            site_sub_brand: '',
            site_version: process.env.NEXT_PUBLIC_GIT_HASH,
          }}
        >
          <MessagesProvider
            locale={awazeHttpHeaders.contentfulLocale}
            contentfulTranslations={labels}
          >
            <GlobalProps.Provider
              value={{
                optimizelySdkKey,
                ...awazeHttpHeaders,
                experiments,
                pageType,
                awazeClient,
              }}
            >
              <GenericPageLayout
                isPreview={isPreview}
                brand={brand}
                contentfulId={contentfulId}
                header={header}
                footer={footer}
                headerOptions={headerOptions}
                searchFormConfig={searchFormConfig}
                newAWSAccount={awazeHttpHeaders.newAWS}
                breadcrumbs={breadcrumbs}
                showPhoneIcon={showPhoneIcon}
              >
                {/** TODO: Check custom head props and remove what we don't use */}
                <CustomHead
                  seoData={{
                    seoTitle,
                    seoDescription,
                    seoNoindex: seoNoIndex,
                    seoKeywords,
                  }}
                  host={awazeHttpHeaders.host}
                  url={awazeHttpHeaders.slug}
                  hrefLangs={hrefLangs}
                  schemaMarkup={schemaMarkup}
                />
                <LcpImagePreload lcpImage={lcpImage} />
                {isPreview && !!errors?.length && (
                  <ModuleErrors errors={errors} />
                )}
                <ConditionalWrapper
                  condition={!!blogData?.author}
                  Wrapper={'article'}
                >
                  {sections?.map(
                    ({ name, layout, content, contentType }, index) => (
                      <ContentfulIdentifier
                        key={`${name}-${index}`}
                        isPreview={isPreview}
                        contentfulId={layout.contentfulId}
                      >
                        {index === 2 &&
                          ['Destination', 'Theme'].includes(pageType) && (
                            <ThumbnailMap />
                          )}
                        <ContentWrapper
                          contentType={contentType}
                          content={content}
                          layout={layout}
                        />
                      </ContentfulIdentifier>
                    ),
                  )}
                </ConditionalWrapper>
              </GenericPageLayout>
            </GlobalProps.Provider>
          </MessagesProvider>
        </TmsProvider>
      </PlacesProvider>
    </GuestAuthProvider>
  )
}

const brandTagOf = (brand: SupportedBrand): string => {
  switch (brand) {
    case 'cottages.com':
      return 'brandCottages'
    case 'hoseasons':
      return 'brandHoseasons'
    case 'novasol':
      return 'brandNovasol'
    case 'fincallorca':
      return 'brandFincallorca'
  }
  throw new Error(`Unsupported brand ${brand}`)
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

//  Url will look like this:
//  /static/module/<encodedSlug>/<host>/<brand>/<language>/<country>/<marketCode>/<currency>/<headerNewAWS>/<ageMigratedBrand>/<encodedExperiments>/<userDeviceType>
export const getStaticProps: GetStaticProps = async (context) => {
  const incomingParams = Array.isArray(context.params.slug)
    ? context.params.slug
    : []

  const [
    encodedSlug,
    host,
    headerBrand,
    headerLanguage,
    headerCountry,
    headerMarketCode,
    headerCurrency,
    headerNewAWS,
    headerClient,
    ageMigratedBrand,
    encodedExperiments,
    userDeviceType,
  ] = incomingParams

  // Handle the case when encodedExperiments is undefined or not valid JSON
  let experiments = null
  try {
    experiments = encodedExperiments
      ? JSON.parse(decodeURIComponent(encodedExperiments))
      : {}
  } catch (e) {
    experiments = {}
    logger.error('Error parsing experiments:', e)
  }
  const revalidate = REVALIDATE_SECONDS

  let slug = ''
  try {
    slug = decodeURIComponent(encodedSlug)
  } catch (e) {
    logger.error(`Error decoding slug:`, {
      encodedSlug,
      host,
      headerBrand,
      headerLanguage,
      headerCountry,
      error: e,
    })
    return {
      redirect: {
        permanent: true,
        destination: `/404`,
      },
      revalidate,
    }
  }
  const xHeaders = {
    'x-awaze-brand': headerBrand,
    'x-awaze-currency': headerCurrency,
    'x-awaze-market-code': headerMarketCode,
    'x-awaze-language': headerLanguage,
    'x-awaze-country': headerCountry,
    'x-awaze-aws': headerNewAWS,
    'x-awaze-client': headerClient,
  }

  const {
    brand,
    language: languageHeader,
    country,
    currency,
    marketCode,
    newAWS,
    headersWarnings,
    awazeClient,
  } = validateHeaders(xHeaders)

  const language = Array.isArray(languageHeader)
    ? languageHeader[0]
    : languageHeader

  // Log missing HEADER values
  if (headersWarnings?.length) {
    logWarnings(headersWarnings)
  }

  if (isEmptyPage(slug)) {
    logger.error('Empty slug', {
      slug,
      brand,
      language,
      country,
      currency,
      marketCode,
      newAWS,
      host,
    })
    return {
      redirect: {
        permanent: true,
        destination: `/404`,
      },
      revalidate,
    }
  }

  const isPreview = context.draftMode || false
  const contentfulClient = await contentfulFactory().getClient(isPreview)
  if (!contentfulClient) {
    logger.error('Contentful client not found', {
      brand,
      language,
      country,
      currency,
      marketCode,
      newAWS,
      slug,
      host,
    })
    return {
      redirect: {
        permanent: false,
        destination: `/404`,
      },
      revalidate,
    }
  }

  const globalContentfulClient = await contentfulGlobalFactory().getClient(
    isPreview,
  )

  const globalContentfulLocale = await getLocaleFromContentful(
    country,
    language,
    globalContentfulClient,
  )

  const globals = await globalContentfulClient.getEntries<GlobalFields>({
    'metadata.tags.sys.id[in]': brandTagOf(brand),
    content_type: 'landingPageGlobals',
    include: 10,
    locale: globalContentfulLocale,
  })
  let globalFields = null
  if (globals.total !== 1) {
    logger.error(
      `Expected 1 global field, but found ${globals.total} for brand ${brand}`,
    )
  } else {
    globalFields = globals.items[0].fields
  }

  if (!globalFields) {
    logger.error(
      `No global fields found for brand ${brand} and locale ${globalContentfulLocale}`,
    )
    return {
      redirect: {
        permanent: false,
        destination: `/404`,
      },
      revalidate,
    }
  }

  const contentfulLocale = await getLocaleFromContentful(
    country,
    language,
    contentfulClient,
  )

  const awazeHttpHeaders = {
    brand,
    contentfulLocale,
    marketCode,
    language,
    country,
    currency,
    newAWS,
    slug,
    host,
  }

  const { unparsedSearchConfig, header, footer, labels } =
    mapGlobalFields(globalFields)

  const interstitialPageData = await getInterstitialPage(
    ageMigratedBrand,
    brand,
    contentfulLocale,
    contentfulClient,
  )

  const content = await getPageModuleFromContentful(
    contentfulClient,
    slug,
    brand,
    contentfulLocale,
    isPreview,
  )

  if (!content) {
    logger.info(
      `No content found for slug ${slug} and brand ${brand} and locale ${contentfulLocale} ${JSON.stringify(
        {
          experiments,
        },
      )}`,
    )
    return {
      redirect: {
        permanent: false,
        destination: `/404`,
      },
      revalidate,
    }
  }

  const { pageType } = content

  const isBlog = pageType === 'Blog' && !!content?.author

  const blogData: BlogData | Record<string, undefined> = isBlog
    ? getUtagDataForBlogPage(content)
    : {}

  // eslint-disable-next-line prefer-const
  let { sections, errors, lcpImage } = await getSections(content, isPreview, {
    ...awazeHttpHeaders,
    pageType,
    oneSearchSalt: Date.now().toString(),
    locationTag: content.locationTag,
    locationName: content.locationName,
    isPreview,
    timeToRead: blogData?.timeToRead,
    blogTags: blogData?.blogTags,
    ioc: {
      fetchProperties,
      contentfulClient,
    },
    experiments,
  })

  if (pageType === 'Destination') {
    // (1) For theme pages, we don't want the destination to show up in the search bar,
    unparsedSearchConfig.destination = content?.locationName
  }
  // (2) but we still want to do the search on locationTag if it is provided.
  unparsedSearchConfig.destinationId = content.locationTag

  unparsedSearchConfig.placesId = content.placesId

  const optimizelySdkKey = process.env.OPTIMIZELY_SDK_KEY || ''

  // Combine all errors for preview
  const allErrors = isPreview ? errors.concat(content.errors || []) : []

  const breadcrumbs = ['Destination', 'Theme', 'Blog', 'Collections'].includes(
    pageType,
  ) //pageTypes to show breadcrumbs
    ? await getBreadcrumbFromSlug(
        contentfulClient,
        slug,
        brand,
        contentfulLocale,
      )
    : null

  const hrefLangs = getCurrentPublicRuntimeConfig()[brand]?.['addHrefHTMLtags']
    ? await getPageSlugFromAllLocales(contentfulClient, content.id)
    : null

  const brandsWithPhoneIcon = ['fincallorca', 'novasol']
  const showPhoneIcon =
    headerLanguage === 'de' && brandsWithPhoneIcon.includes(brand)

  return {
    props: {
      contentfulId: content.contentfulInternalId,
      isPreview,
      sections,
      errors: allErrors,
      headerOptions: removeUndef(content.headerOptions?.fields) || null,
      awazeHttpHeaders,
      brand: headerBrand,
      seoTitle: content.seoTitle || null,
      seoDescription: content.seoDescription || null,
      seoNoIndex: content.seoNoindex || null,
      seoKeywords: content.seoKeywords || [],
      header: removeSysAndMetadata(removeUndef(header)),
      footer: removeSysAndMetadata(removeUndef(footer)),
      unparsedSearchConfig: removeUndef(unparsedSearchConfig),
      lcpImage,
      breadcrumbs,
      labels,
      optimizelySdkKey,
      interstitialPageData,
      blogData,
      experiments,
      pageType,
      hrefLangs,
      schemaMarkup: getPageSchemaMarkup(content, host, breadcrumbs, isBlog),
      userDeviceType,
      awazeClient,
      showPhoneIcon,
    },
    revalidate,
  }
}
